import { Paper, Box, Grid2, Typography, CircularProgress, IconButton, Button, Snackbar, Alert, Badge, Tooltip } from '@mui/material';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { hasValidAddress } from '../utils/homeUtils';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import AddHomeForm from '../components/AddHomeForm';
import DocumentsModal from '../components/DocumentsModal';
import ImageSlider from '../components/ImageSlider';
import TasksList from '../components/TasksList';
import HomeInfo from '../components/HomeInfo';
import ChatPanelHome from '../components/ChatPanelHome';
import { useHomeAttachmentsContext } from '../contexts/HomeAttachmentsContext';
import UploadStatusOverlay from '../components/UploadStatusOverlay';
import { useHomeDetailsContext } from '../contexts/HomeDetailsContext';
import HomeMainImage from '../components/HomeMainImage';
import GeneratedRooms from '../components/GeneratedRooms';
import AllImagesModal from '../components/AllImagesModal';
import CameraCapture from '../components/mobile/CameraCapture';
import RepairCostEstimation from '../components/RepairCostEstimation';
import BuildIcon from '@mui/icons-material/Build';
import { useHomeRepairsContext } from '../contexts/HomeRepairsContext';
import { useHomeIntelligenceOptional } from '../components/intelligence';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { usePropertyReport } from '../hooks/usePropertyReport';

interface HomeDetailsDesktopProps {
  isDetailView: boolean;
}

function HomeDetailsDesktop({ isDetailView }: HomeDetailsDesktopProps) {
  const navigate = useNavigate();
  const { homeId } = useParams<{ homeId: string }>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isTasksExpanded, setIsTasksExpanded] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [fileCount, setFileCount] = useState(0);
  const [showGeneratedRooms, setShowGeneratedRooms] = useState(false);
  const [allImagesModalOpen, setAllImagesModalOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [showRepairEstimates, setShowRepairEstimates] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);

  // Use the home details context instead of local state and useEffect
  const { 
    homeData, 
    loading, 
    error, 
    isSubmitting,
    editHome 
  } = useHomeDetailsContext();

  // Check if intelligence is available AND home has valid address
  const intelligenceContext = useHomeIntelligenceOptional();
  const hasAddress = hasValidAddress(homeData?.details);

  // Use the home attachments context
  const { 
    uploadFiles,
    uploadDocuments,
    isUploading, 
    isProcessing,
    isUploadingDocuments,
    uploadProgress,
    documentUploadProgress,
    ALLOWED_DOCUMENT_TYPES,
    attachments,
    isDocumentFile,
  } = useHomeAttachmentsContext();

  // Add repairs context to get repair count
  const { repairs } = useHomeRepairsContext();
  
  // Calculate unresolved repairs count
  const unresolvedRepairsCount = repairs.filter(repair => !repair.is_resolved).length;
  
  // Calculate document count
  const documentCount = attachments.filter(att => isDocumentFile(att.fileType)).length;

  // Property report generation hook
  const { generateReport, isGenerating: isGeneratingReport, error: reportError, clearError: clearReportError } = usePropertyReport();

  // Handle report generation
  const handleGenerateReport = async () => {
    if (!homeId) return;
    clearReportError();
    await generateReport(homeId);
  };

  // Show error snackbar when report generation fails
  useEffect(() => {
    if (reportError) {
      setSnackbar({
        open: true,
        message: reportError,
        severity: 'error'
      });
      clearReportError();
    }
  }, [reportError, clearReportError]);

  // Wrapper for uploadFiles that tracks file count
  const handleUploadFiles = (files: FileList | null) => {
    if (files) {
      setFileCount(files.length);
      return uploadFiles(files);
    }
    return false;
  };

  // Wrapper for uploadDocuments that tracks file count
  const handleUploadDocuments = (files: FileList | null) => {
    if (files) {
      setFileCount(files.length);
      return uploadDocuments(files);
    }
    return false;
  };

  const handleCancelUpload = () => {
    // If you need to implement upload cancellation, do it here
    // You would need to add a cancel function to useHomeAttachments
    console.log('Upload cancellation not implemented');
  };

  const handleBack = () => {
    navigate('/homes');
  };

  const handleEditOpen = () => {
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  const handleEditSubmit = async (homeName: string, address: string, details: {
    bedrooms: number;
    bathrooms: number;
    sqft: number;
    price: number;
  }) => {
    const result = await editHome(homeName, address, details);
    
    if (result.success) {
      handleEditClose();
      setSnackbar({
        open: true,
        message: 'Home updated successfully',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to update home',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const toggleChatExpanded = () => {
    setIsChatExpanded(prev => !prev);
  };

  const toggleTasksExpanded = () => {
    setIsTasksExpanded(prev => !prev);
  };


  const handleOpenCamera = () => {
    setCameraOpen(true);
  };

  const handleCloseCamera = () => {
    setCameraOpen(false);
  };

  const handleOpenDocuments = () => {
    setDocumentsModalOpen(true);
  };

  const handleCloseDocuments = () => {
    setDocumentsModalOpen(false);
  };

  const handleCapturedImages = async (files: File[]) => {
    if (files.length > 0) {
      setFileCount(files.length);
      return uploadFiles(files);
    }
    return false;
  };

  const parseHomeDetails = () => {
    let address = '';
    let bedrooms = 3;
    let bathrooms = 2;
    let sqft = 1800;
    let price = 350000;
    
    if (homeData?.details) {
      try {
        // Check if it's already a JSON object
        const details = typeof homeData.details === 'string' 
          ? JSON.parse(homeData.details) 
          : homeData.details;
        
        address = details.address || '';
        bedrooms = details.bedrooms || 3;
        bathrooms = details.bathrooms || 2;
        sqft = details.sqft || 1800;
        price = details.price || 350000;
      } catch (e) {
        // If parsing fails, assume the entire details is just the address
        console.error('Error parsing home details:', e);
        address = homeData.details;
      }
    }
    
    return { address, bedrooms, bathrooms, sqft, price };
  };

  return (
    <Box sx={{ 
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      p: 3,
      pl: { xs: 4, sm: 5 },
      bgcolor: 'background.default',
      width: '100%',
      overflow: 'hidden'
    }}>
      {isDetailView && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2, // Reduced margin
          ml: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={handleBack}
              color="primary"
              sx={{ 
                mr: 1,
                bgcolor: 'rgba(25, 118, 210, 0.08)',
                width: 36,
                height: 36,
                borderRadius: '50%',
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.15)'
                }
              }}
              aria-label="back to homes"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 500,
                color: 'primary.dark',
                fontFamily: '"Roboto Slab", "Roboto", "Helvetica", "Arial", sans-serif',
                textAlign: 'left'
              }}
            >
              {loading ? 'Loading...' : homeData?.chat_name || 'Home Details'}
            </Typography>
          </Box>
          
          {/* Documents button - only show if documents exist */}
          {documentCount > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DescriptionIcon />}
              onClick={handleOpenDocuments}
              sx={{
                borderRadius: '8px',
                fontWeight: 500,
                boxShadow: 'none',
                textTransform: 'none',
                px: 2,
                py: 0.5,
                mr: 1
              }}
            >
              Documents ({documentCount})
            </Button>
          )}
        </Box>
      )}
      
      {isDetailView ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
            <Grid2 container spacing={2} sx={{
              flex: 1,
              width: '100%',
              m: 0,
              overflow: 'hidden'
            }}>
              {/* Left half */}
              <Grid2 size={{xs:12, md: 6}} sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden'
              }}>
                {showGeneratedRooms ? (
                  <GeneratedRooms 
                    onImageSelect={(imageId) => {
                      console.log(`Selected image: ${imageId}`);
                    }} 
                    onClose={() => setShowGeneratedRooms(false)}
                  />
                ) : showRepairEstimates ? (
                  // Updated component call - no estimates prop needed
                  <RepairCostEstimation
                    onClose={() => setShowRepairEstimates(false)}
                  />
                ) : (
                  <>
                    {/* Fixed height section containing all non-scrollable content */}
                    <Box sx={{ flex: '0 0 auto' }}> {/* Important: this contains all fixed elements */}
                      {/* Buttons row - Upload Photos and Edit */}
                      <Box sx={{ 
                        mb: 1.5,
                        display: 'flex', 
                        justifyContent: 'flex-start', 
                        gap: 2 
                      }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={(isUploading || isProcessing) ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                          onClick={() => document.getElementById('photo-upload-input')?.click()}
                          disabled={isUploading || isProcessing || isUploadingDocuments}
                          sx={{
                            borderRadius: '8px',
                            fontWeight: 500,
                            boxShadow: 'none',
                            textTransform: 'none',
                            px: 2,
                            py: 0.5
                          }}
                        >
                          {isUploading ? 'Uploading...' : isProcessing ? 'Processing...' : 'Upload Photos'}
                        </Button>
                        
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={isUploadingDocuments ? <CircularProgress size={16} /> : <DescriptionIcon />}
                          onClick={() => document.getElementById('document-upload-input')?.click()}
                          disabled={isUploading || isProcessing || isUploadingDocuments}
                          sx={{
                            borderRadius: '8px',
                            fontWeight: 500,
                            boxShadow: 'none',
                            textTransform: 'none',
                            px: 2,
                            py: 0.5
                          }}
                        >
                          {isUploadingDocuments ? 'Uploading...' : 'Upload Documents'}
                        </Button>
                        
                        {/* Add Camera Button */}
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<CameraAltIcon />}
                          onClick={handleOpenCamera}
                          disabled={isUploading || isProcessing || isUploadingDocuments}
                          sx={{
                            borderRadius: '8px',
                            fontWeight: 500,
                            boxShadow: 'none',
                            textTransform: 'none',
                            px: 2,
                            py: 0.5
                          }}
                        >
                          Take Photos
                        </Button>
                        
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={handleEditOpen}
                          sx={{
                            borderRadius: '8px',
                            fontWeight: 500,
                            boxShadow: 'none',
                            textTransform: 'none',
                            px: 2,
                            py: 0.5
                          }}
                        >
                          Edit Home
                        </Button>
                        <input
                          id="photo-upload-input"
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            handleUploadFiles(e.target.files);
                            e.target.value = '';
                          }}
                        />
                        <input
                          id="document-upload-input"
                          type="file"
                          accept={ALLOWED_DOCUMENT_TYPES.join(',')}
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            handleUploadDocuments(e.target.files);
                            e.target.value = '';
                          }}
                        />
                      </Box>
                      
                      {/* Main image */}
                      <HomeMainImage />

                      {/* Buttons row */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start', // Changed from 'space-between' to 'flex-start'
                        mb: 0,
                        mt: 1.5, 
                        width: '100%',
                        gap: 1
                      }}>
                        <Button 
                          variant="outlined"
                          color="secondary"
                          sx={{
                            borderRadius: '8px',
                            fontWeight: 500,
                            textTransform: 'none',
                            py: 0.5,
                            px: 1.5
                          }}
                          onClick={() => setShowGeneratedRooms(prev => !prev)}
                        >
                          {showGeneratedRooms ? 'Hide Rooms' : 'Generated Rooms'}
                        </Button>
                        
                        <Badge 
                          badgeContent={unresolvedRepairsCount} 
                          color="warning"
                          max={99}
                          invisible={unresolvedRepairsCount === 0}
                          sx={{
                            '& .MuiBadge-badge': {
                              right: 8,
                              top: 8,
                              fontSize: '0.65rem',
                              height: 16,
                              minWidth: 16
                            }
                          }}
                        >
                          <Button 
                            variant="outlined"
                            color="warning"
                            startIcon={<BuildIcon />}
                            sx={{
                              borderRadius: '8px',
                              fontWeight: 500,
                              textTransform: 'none',
                              py: 0.5,
                              px: 1.5
                            }}
                            onClick={() => {
                              setShowRepairEstimates(prev => !prev);
                            }}
                          >
                            {showRepairEstimates ? 'Hide Repairs' : 'Repair Costs'}
                          </Button>
                        </Badge>

                        {/* Intelligence Button - only show if intelligence context is available */}
                        {intelligenceContext && (
                          <Badge
                            badgeContent={intelligenceContext.intelligence?.propertyScore?.overall ? Math.round(intelligenceContext.intelligence.propertyScore.overall) : 0}
                            color="primary"
                            max={99}
                            invisible={!intelligenceContext.intelligence}
                            sx={{
                              '& .MuiBadge-badge': {
                                right: 8,
                                top: 8,
                                fontSize: '0.65rem',
                                height: 16,
                                minWidth: 16
                              }
                            }}
                          >
                            <Tooltip
                              title={hasAddress ? "" : "Add a home address to enable Property Intelligence"}
                              arrow
                              placement="top"
                            >
                              <span> {/* Wrapper span needed for tooltip on disabled button */}
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<AssessmentIcon />}
                                  aria-label="View Property Intelligence"
                                  disabled={!hasAddress}
                                  sx={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    py: 0.5,
                                    px: 1.5,
                                    opacity: hasAddress ? 1 : 0.6,
                                  }}
                                  onClick={() => {
                                    if (hasAddress) {
                                      navigate(`/home/${homeId}/intelligence`);
                                    }
                                  }}
                                >
                                  Property Intel
                                </Button>
                              </span>
                            </Tooltip>
                          </Badge>
                        )}

                        {/* Property Report Button */}
                        <Tooltip
                          title="Generate a professional property condition report PDF"
                          arrow
                          placement="top"
                        >
                          <span> {/* Wrapper span needed for tooltip on disabled button */}
                            <Button
                              variant="outlined"
                              color="success"
                              startIcon={isGeneratingReport ? <CircularProgress size={16} color="inherit" /> : <SummarizeIcon />}
                              disabled={isGeneratingReport}
                              sx={{
                                borderRadius: '8px',
                                fontWeight: 500,
                                textTransform: 'none',
                                py: 0.5,
                                px: 1.5,
                              }}
                              onClick={handleGenerateReport}
                              data-testid="generate-report-button"
                            >
                              {isGeneratingReport ? 'Generating...' : 'Property Report'}
                            </Button>
                          </span>
                        </Tooltip>

                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{
                            borderRadius: '50px',
                            fontWeight: 500,
                            textTransform: 'none',
                            px: 2.5,
                            py: 0.5,
                            ml: 'auto' // Add this to push the "All Images" button to the right
                          }}
                          onClick={() => setAllImagesModalOpen(true)}
                        >
                          All Images
                        </Button>
                      </Box>

                      {/* Image Slider with constrained height */}
                      <Box sx={{ height: '120px', mb: 0.5 }} >
                        <ImageSlider height={80} />
                      </Box>
                    </Box>
                    
                    {/* HomeInfo with scrolling - will take remaining space */}
                    <Box sx={{ 
                      flex: 1,
                      overflow: 'auto',
                      mb: 0,
                    }}>
                      <HomeInfo 
                        name={homeData?.chat_name || ''}
                        details={homeData?.details || ''}
                        error={error}
                      />
                    </Box>
                  </>
                )}
              </Grid2>
              
              {/* Right half */}
              <Grid2 size={{xs: 12, md: 6}} sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: 1,
                position: 'relative',
                overflow: 'hidden'
              }}>

                {/* TasksList with constrained height and scrolling */}
                {!isChatExpanded && (
                  <Paper
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      flex: isTasksExpanded ? 1 : '0.4',
                      minHeight: 0,
                      position: 'relative',
                      width: '100%',
                      transition: 'flex 0.5s ease-in-out',
                      flexBasis: isTasksExpanded ? '100%' : '0%',
                      mt: 0,
                      alignSelf: 'flex-start',
                      marginBottom: 'auto',
                      height: isTasksExpanded ? '100%' : 'auto',
                      borderRadius: 2,
                      borderBottomRightRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    elevation={isTasksExpanded ? 4 : 3}
                  >
                    <TasksList 
                      isExpanded={isTasksExpanded}
                      onToggleExpand={toggleTasksExpanded}
                    />
                  </Paper>
                )}
                
                {/* Chat panel */}
                <Paper
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    flex: isChatExpanded ? 1 : isTasksExpanded ? '0' : '0.6',
                    minHeight: 0,
                    position: 'relative',
                    width: '100%',
                    transition: 'flex 0.5s ease-in-out',
                    flexBasis: isChatExpanded ? '100%' : '0%',
                    mb: 0,
                    alignSelf: 'flex-end',
                    marginTop: isTasksExpanded ? '0' : 'auto',
                    height: isChatExpanded ? '100%' : 'auto',
                    //display: isTasksExpanded ? 'none' : 'flex' // Hide chat when tasks are expanded
                  }}
                  elevation={isChatExpanded ? 4 : 3}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1, 
                    borderColor: 'divider',
                    backgroundColor: 'background.header',
                  }}>
                    <Typography variant="h6" sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                    }}>
                      Chat with CribCritic AI
                    </Typography>
                    <IconButton 
                      onClick={toggleChatExpanded}
                      size="small"
                      color="primary"
                      aria-label={isChatExpanded ? "collapse chat" : "expand chat"}
                    >
                      {isChatExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                  </Box>
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto', // Enable scrolling for chat content
                    height: '100%'
                  }}>
                    <ChatPanelHome  />
                  </Box>
                </Paper>
              </Grid2>
            </Grid2>
        )
      ) : (
        // Render child routes when not in detail view
        <Outlet />
      )}

      {/* Add Home Edit Form */}
      <AddHomeForm
        open={editModalOpen}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        isSubmitting={isSubmitting}
        isEdit={true}
        initialValues={{
          name: homeData?.chat_name || '',
          ...parseHomeDetails()
        }}
        title="Edit Home"
        submitButtonText="Update"
      />

      <Snackbar 
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <UploadStatusOverlay
        isUploading={isUploading}
        isProcessing={isProcessing}
        isUploadingDocuments={isUploadingDocuments}
        progress={uploadProgress}
        documentProgress={documentUploadProgress}
        fileCount={fileCount}
        onCancel={handleCancelUpload}
      />

      <AllImagesModal
        open={allImagesModalOpen}
        onClose={() => setAllImagesModalOpen(false)}
      />

      <CameraCapture
        open={cameraOpen}
        onClose={handleCloseCamera}
        onCapture={handleCapturedImages}
        isUploading={isUploading}
      />

      <DocumentsModal
        open={documentsModalOpen}
        onClose={handleCloseDocuments}
      />
    </Box>
  );
}

export default HomeDetailsDesktop;